import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef, useEffect } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import styles from './TickerText.module.scss';
import { setupTransitionInTimeline } from './TickerText.transitions';
import type { TickerTextProps } from './TickerText.data';
import TickerTextItem from './ticker-text-item/TickerTextItem';
import { createLoopingTicker } from '../../../util/animation/timeline.utils';

export default function TickerText({ className, blok, ref }: TickerTextProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLElement | null>;
  const itemsRef = useRef<Array<HTMLElement>>([]);
  const timeline = useRef<gsap.core.Timeline>(undefined);

  const { inView } = useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
      enableInView: true,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useEffect(() => {
    const elements = itemsRef?.current?.map((item) => item);
    if (elementRef?.current) {
      timeline.current = createLoopingTicker(elements as Array<HTMLElement>, elementRef.current);
    }
  }, [elementRef, timeline, itemsRef]);

  useEffect(() => {
    if (inView) {
      timeline?.current?.play();
    } else {
      timeline?.current?.pause();
    }
  }, [inView, elementRef]);

  return (
    <div
      ref={elementRef as RefObject<HTMLDivElement | null>}
      className={classNames(styles.tickerText, className)}
      {...sbEditable(blok)}
    >
      {blok?.items?.map((data, index) => (
        <TickerTextItem
          ref={(ref) => {
            itemsRef.current[index] = ref as HTMLElement;
          }}
          blok={data}
          key={data._uid}
        />
      ))}
    </div>
  );
}
